/**
 * Unread-notification counts are capped at this value for badge display. A
 * returned count equal to {@link NOTIFICATION_UNREAD_COUNT_CAP} means "at least
 * this many" — clients should render it as `${NOTIFICATION_UNREAD_COUNT_CAP}+`.
 *
 * The cap lets the server bound the count query (it stops counting past the cap
 * instead of scanning an entire unread backlog), while remaining lossless for
 * badges that already clamp their display. Matches the per-type cap applied to
 * {@link NotificationCounts.byType}.
 */
export const NOTIFICATION_UNREAD_COUNT_CAP = 99;
export class NotificationsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Get notifications. `unreadCount` is capped at
     * {@link NOTIFICATION_UNREAD_COUNT_CAP} (a value equal to the cap means "at
     * least that many").
     */
    async getNotifications(options) {
        const params = new URLSearchParams();
        if (options?.limit)
            params.set('limit', options.limit.toString());
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.since)
            params.set('since', options.since);
        if (options?.unreadOnly)
            params.set('unreadOnly', 'true');
        if (options?.types)
            params.set('types', options.types.join(','));
        const query = params.toString();
        return this.client.get(`/api/notifications${query ? `?${query}` : ''}`);
    }
    /**
     * Mark notifications as read.
     */
    async markAsRead(notificationIds) {
        return this.client.post('/api/notifications/read', { notificationIds });
    }
    /**
     * Mark conversation thread notifications read up to a message id.
     * Covers DM messages, replies, and reactions the caller has already read.
     */
    async markConversationThreadRead(params) {
        return this.client.post('/api/notifications/read-conversation', params);
    }
    /**
     * Mark notifications as unread.
     */
    async markAsUnread(notificationIds) {
        return this.client.post('/api/notifications/unread', { notificationIds });
    }
    /**
     * Delete notifications.
     */
    async deleteNotifications(notificationIds) {
        return this.client.delete('/api/notifications', { body: { notificationIds } });
    }
    /**
     * Get unread notification counts.
     */
    async getCounts() {
        return this.client.get('/api/notifications/count');
    }
    /**
     * Unified notifications inbox: notification rows merged with the member's
     * projected Space `@here`/`@role` mentions, newest-first. `cursor` pages older
     * items (opaque `_id` cursor from a previous page's `nextCursor`).
     */
    async getInbox(options) {
        const params = new URLSearchParams();
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.limit)
            params.set('limit', options.limit.toString());
        const query = params.toString();
        return this.client.get(`/api/notifications/inbox${query ? `?${query}` : ''}`);
    }
    /** Bell badge: inbox items newer than the seen cursor that are still unread. */
    async getInboxUnseenCount() {
        return this.client.get('/api/notifications/inbox/count');
    }
    /** Advance the inbox seen cursor (clears the bell "new" indicator). */
    async markInboxSeen(params) {
        return this.client.post('/api/notifications/inbox/seen', params ?? {});
    }
}
//# sourceMappingURL=notifications-api.js.map